import { collapseRepeatedTableCellText, normalizeHtml } from "../html";

const stripInvalidCssLengths = (inputHtml) => {
    if (!inputHtml) return inputHtml;
    let sanitized = inputHtml;
    // Remove only style properties with clearly malformed values like "66-7.1pt00" (digit-minus-digit, not negative numbers or decimals)
    sanitized = sanitized.replace(
        /\b(?:width|height|font-size|line-height|margin(?:-(?:top|right|bottom|left))?|padding(?:-(?:top|right|bottom|left))?|border(?:-(?:top|right|bottom|left))?-width)\s*:\s*[^;]*\d+-\d\.?\d*[^;]*;?/gi,
        ""
    );
    // Remove width/height attributes with same malformed pattern
    sanitized = sanitized.replace(
        /\s(?:width|height)=["']?[^"'\s>]*\d+-\d\.?\d*[^"'\s>]*["']?/gi,
        ""
    );
    return sanitized;
};

/**
 * html-to-pdfmake ломает вложенные rowspan (форма 070 и др.): splice по индексу
 * исходной строки сдвигает ячейки — «Анықталған» попадает в колонку «Наименование».
 *
 * Фикс: разворачиваем rowspan в явные пустые <td>, переносим span в class pdf-rowspan-N,
 * после htmlToPdfmake восстанавливаем cell.rowSpan через applyPdfRowspanClasses.
 */
export function expandHtmlTableRowspans(html) {
    if (!html || typeof html !== 'string' || !/rowspan/i.test(html)) {
        return html;
    }
    if (typeof document === 'undefined') {
        return html;
    }

    const container = document.createElement('div');
    container.innerHTML = html;

    container.querySelectorAll('table').forEach((table) => {
        const trList = Array.from(table.querySelectorAll('tr'));
        if (trList.length === 0) return;

        const rowCells = trList.map((tr) =>
            Array.from(tr.children).filter((el) => {
                const tag = el.tagName;
                return tag === 'TD' || tag === 'TH';
            })
        );

        const placements = []; // { r, c, el, rowSpan, colSpan }

        for (let r = 0; r < rowCells.length; r++) {
            let c = 0;
            const blocked = new Set();
            for (const p of placements) {
                if (r > p.r && r < p.r + p.rowSpan) {
                    for (let k = 0; k < p.colSpan; k++) blocked.add(p.c + k);
                }
            }

            for (const el of rowCells[r]) {
                while (blocked.has(c)) c++;
                const colSpan = Math.max(1, parseInt(el.getAttribute('colspan') || '1', 10) || 1);
                const rowSpan = Math.max(1, parseInt(el.getAttribute('rowspan') || '1', 10) || 1);
                placements.push({ r, c, el, rowSpan, colSpan });
                for (let k = 0; k < colSpan; k++) blocked.add(c + k);
                c += colSpan;
            }
        }

        // rebuild each row with full column set
        for (let r = 0; r < trList.length; r++) {
            const tr = trList[r];
            const originsHere = placements.filter((p) => p.r === r);
            const cellsByCol = new Map();
            for (const p of originsHere) {
                cellsByCol.set(p.c, p);
            }

            // columns covered by rowspan from above → empty placeholder
            const coveredByAbove = new Set();
            for (const p of placements) {
                if (r > p.r && r < p.r + p.rowSpan) {
                    for (let k = 0; k < p.colSpan; k++) coveredByAbove.add(p.c + k);
                }
            }

            // compute max col for this row
            let rowMax = 0;
            for (const p of placements) {
                if (r >= p.r && r < p.r + p.rowSpan) {
                    rowMax = Math.max(rowMax, p.c + p.colSpan);
                }
            }

            while (tr.firstChild) tr.removeChild(tr.firstChild);

            let c = 0;
            while (c < rowMax) {
                if (coveredByAbove.has(c)) {
                    const td = document.createElement('td');
                    tr.appendChild(td);
                    c++;
                    continue;
                }
                const p = cellsByCol.get(c);
                if (p) {
                    const el = p.el;
                    if (p.rowSpan > 1) {
                        el.removeAttribute('rowspan');
                        const prev = el.getAttribute('class') || '';
                        const cls = `pdf-rowspan-${p.rowSpan}`;
                        el.setAttribute('class', prev ? `${prev} ${cls}` : cls);
                    }
                    tr.appendChild(el);
                    c += p.colSpan;
                } else {
                    // gap (shouldn't happen often)
                    const td = document.createElement('td');
                    tr.appendChild(td);
                    c++;
                }
            }
        }
    });

    return container.innerHTML;
}

/** После htmlToPdfmake: class pdf-rowspan-N → cell.rowSpan = N */
export function applyPdfRowspanClasses(node) {
    if (!node) return;

    if (Array.isArray(node)) {
        node.forEach(applyPdfRowspanClasses);
        return;
    }

    if (typeof node !== 'object') return;

    const styles = node.style;
    if (Array.isArray(styles)) {
        for (const s of styles) {
            if (typeof s === 'string' && s.startsWith('pdf-rowspan-')) {
                const n = parseInt(s.slice('pdf-rowspan-'.length), 10);
                if (n > 1) node.rowSpan = n;
            }
        }
    }

    if (node.table?.body) {
        applyPdfRowspanClasses(node.table.body);
    }
    if (node.stack) {
        applyPdfRowspanClasses(node.stack);
    }
    if (Array.isArray(node.text)) {
        applyPdfRowspanClasses(node.text);
    }
}

export function normalizeHtmlTable(html, version) {
    html = collapseRepeatedTableCellText(html);
    html = normalizeHtml(html);
                
    //   const e = htmlToPdfmake(html);
    //    content.push([e])
    html = html.replaceAll(`<font face="Times New Roman" size="3"`, `<font face="Times New Roman" size="1"`)
    //console.log(html);
    while (html.indexOf('  ') > 0)
        html = html.replaceAll('  ', ' ');
    html = html.replaceAll('; ', ';');
    html = html.replaceAll(': ', ':');

    html = html.replaceAll(`<font face="Times New Roman" size="3"`, `<font face="Times New Roman" size="1"`);
    html = html.replaceAll(`font-family:&quot;Times New Roman&quot;,serif;mso-fareast-font-family:&quot;Times New Roman&quot;;`, ``)
    // //font-family: &quot;Times New Roman&quot;,serif;mso-fareast-font-family:&quot;Times New Roman&quot;      
    //html = html.replaceAll(`font-family:&quot;Times New Roman&quot;,serif;mso-fareast-font-family:&quot;Times New Roman&quot;`, `` )
    html = html.replaceAll(`font-family:&quot;Times New Roman&quot;,serif`, ``);
    html = html.replaceAll(`font-family:&quot;Times New Roman&quot;,serif;mso-bidi-font-family:Mangal;`, ``)
    html = html.replaceAll(`font-family:&quot;Times New Roman&quot;,serif;`, ``)
    html = html.replaceAll(`font:7.0pt &quot;Times New Roman&quot;`, ``);
    html = html.replaceAll(`font-family:&quot;Times New Roman&quot;,&quot;serif&quot;`, ``)
    html = html.replaceAll(';mso-fareast-font-family:TimesNewRomanPSMT', '');
    html = html.replaceAll(' class="MsoTableGrid"', '');
    html = html.replaceAll(' class="MsoNormal"', '');
    html = html.replaceAll('class="MsoNormalTable"', '');
    html = html.replaceAll(';mso-ansi-language:KZ', '');
    html = html.replaceAll(' class="MsoNormalCxSpMiddle"', '');
    html = html.replaceAll(';mso-bidi-theme-font:minor-bidi', '');
    html = html.replaceAll('font-family:&quot;inherit&quot;', ``);
    html = html.replaceAll(' class="MsoNoSpacing"', '');
    html = html.replaceAll('<p>​</p>', '');
    html = html.replaceAll('line-height:10⁷%;', '');
    html = html.replaceAll(';line-height:10⁷%', '');
    html = html.replaceAll('line-height:10⁸%;', '');
    html = html.replaceAll(';line-height:10⁸%', '');
    html = html.replaceAll('line-height:10⁹%;', '');
    html = html.replaceAll(';line-height:10⁹%', '');
    html = html.replaceAll('line-height:10⁶%;', '');
    html = html.replaceAll(';line-height:10⁶%', '');
    html = html.replaceAll('line-height:10⁵%;', '');
    html = html.replaceAll(';line-height:10⁵%', '');
    html = html.replaceAll('line-height:10⁴%;', '');
    html = html.replaceAll(';line-height:10⁴%', '');
    html = html.replaceAll('line-height:10³%;', '');
    html = html.replaceAll(';line-height:10³%', '');
    html = html.replaceAll('line-height:10²%;', '');
    html = html.replaceAll(';line-height:10²%', '');
    html = html.replaceAll('line-height:10¹%;', '');
    html = html.replaceAll(';line-height:10¹%', '');
    html = html.replaceAll('line-height:10⁰%;', '');
    html = html.replaceAll(';line-height:10⁰%', '');
    html = html.replaceAll('background-color:initial;', '');

    if (version >= 2) {
        html = html.replaceAll('&nbsp;', '');
        html = html.replaceAll('text-indent:-1.5pt;line-height:normal;', '');
        html = html.replaceAll('text-indent:-1.5pt;line-height:100%;', '');
        html = html.replaceAll('padding:0cm 5.4pt 0cm 5.4pt;height:18.05pt', '');

        //html = stripInvalidCssLengths(html);
        html = expandHtmlTableRowspans(html);
    }

    return html;
}

/** Подставляет корневому <table> width="100%" (убирает старый width при наличии). */
function setTableWidth100(tableBlock) {
    return tableBlock.replace(/(<table)([^>]*)(>)/i, (_, open, attrs, close) => {
        const withoutWidth = attrs.replace(/\s*width=["'][^"']*["']/gi, '').trim();
        return open + (withoutWidth ? ' ' + withoutWidth : '') + ' width="100%"' + close;
    });
}

/**
 * Переводит ширины колонок таблицы в проценты прямо в HTML (до htmlToPdfmake).
 * Так проще отлаживать — можно смотреть итоговый HTML.
 * - Если есть <colgroup><col width="N">... — N переводим в доли от суммы → width="X%"
 * - Если colgroup нет — считаем колонки по первой строке (учитывая colspan), вставляем <colgroup> с равными %
 * - Корневому <table> задаётся width="100%"
 */
export function convertTableColumnWidthsToPercent(html) {
    if (!html || typeof html !== 'string') return html;
    return html.replace(/<table[\s\S]*?<\/table>/gi, (tableBlock) => {
        let out = tableBlock;
        const colgroupMatch = out.match(/<colgroup[^>]*>([\s\S]*?)<\/colgroup>/i);
        if (colgroupMatch) {
            const inner = colgroupMatch[1];
            const widths = [];
            let m;
            const colRe = /<col[^>]*\bwidth=["']?(\d+)/gi;
            while ((m = colRe.exec(inner)) !== null) widths.push(parseInt(m[1], 10));
            if (widths.length > 0) {
                const total = widths.reduce((a, b) => a + b, 0);
                const pcts = total
                    ? widths.map((w) => ((w / total) * 100).toFixed(2) + '%')
                    : widths.map(() => (100 / widths.length).toFixed(2) + '%');
                let i = 0;
                const newInner = inner.replace(/\bwidth=["']?\d+["']?/gi, () => `width="${pcts[i++]}"`);
                out = out.replace(colgroupMatch[0], '<colgroup>' + newInner + '</colgroup>');
            }
        } else {
            const firstTr = out.match(/<tr[^>]*>([\s\S]*?)<\/tr>/i);
            if (firstTr) {
                const rowContent = firstTr[1];
                const tdTags = rowContent.match(/<td[\s\S]*?>/gi);
                if (tdTags && tdTags.length > 0) {
                    let colCount = 0;
                    tdTags.forEach((tag) => {
                        const c = tag.match(/colspan=["']?(\d+)/i);
                        colCount += c ? parseInt(c[1], 10) : 1;
                    });
                    if (colCount > 0) {
                        const pct = (100 / colCount).toFixed(2);
                        const colgroup = '<colgroup>' + `<col width="${pct}%">`.repeat(colCount) + '</colgroup>';
                        out = out.replace(/(<table[^>]*>)/i, '$1' + colgroup);
                    }
                }
            }
        }
        return setTableWidth100(out);
    });
}
